import { useContext, useEffect, useState } from 'react'
import { UserContext, UserContextType } from '../../UserContext'
import { DesktopSourcesListItem } from './DesktopSourcesListItem'
import useMediaSourceActions from '../../functionalities/MediaSources/useMediaSourceActions'
import * as Constants from '../../services/Constants'
import { env } from '../../services/EnvironmentVariables'
import Loader from 'react-loader-spinner'
import { isNullMediaDeviceId, sleep } from '../../utils'
import BottomTray from './BottomTray'
import useMediaPermissionActions from '../../functionalities/mediaPermissions/useMediaPermissionActions'
import useUserContextRef from '../../services/useUserContextRef'
import PermissionRequiredFragment from './PermissionRequiredFragment'
import useAnalytics from '../../services/useAnalytics'
import { AnalyticsEvents } from '../../services/AnalyticsService'
import DiagonalTabs from '@/components/DiagonalTabs'
import useDimensionObserver from '@/services/useDimensionObserver'
import { DialogTitle } from '@/components/Dialog'
import { useNonIdempotentEffect } from '@/hooks/useNonIdempotentEffect'
import ElectronIPC from '@/services/ElectronIPC'

export default function ScreenShareSelectionPage({ onClose }: { onClose: () => void }) {
    const { state } = useContext(UserContext) as UserContextType

    const { userStateRef } = useUserContextRef()

    const { loadCurrentMediaPermissions } = useMediaPermissionActions()

    const { loadDesktopSources } = useMediaSourceActions()

    const { logUserActivityWithContext } = useAnalytics()

    useEffect(() => {
        if (env.IS_ELECTRON) {
            // without the timeout -- too much going on at the same time as
            // when the modal opens, which the modal opening is a heavy process
            // by in itself.
            setTimeout(async () => {
                if (!userStateRef.current.mediaPermissions.desktop) {
                    // try checking permissions one more time
                    await loadCurrentMediaPermissions()
                    await sleep(100)
                    loadDesktopSources()
                } else {
                    loadDesktopSources()
                }
            }, 100)
        }
    }, [])

    return (
        <div className="flex flex-col w-full h-full">
            <div className="flex flex-col overflow-y-auto flex-grow px-4">
                {/* the main screen selection area */}
                <div className="flex gap-3 items-center pb-4">
                    <DialogTitle noPadding>Select Screen to Share</DialogTitle>
                    {state.mediaSources.desktop.length === 0 && (
                        <div className="flex items-center justify-left">
                            <div className="ml-1">
                                <Loader
                                    type="TailSpin"
                                    color="#00BFFF"
                                    width={Constants.spinner_width_md}
                                    height={Constants.spinner_width_md}
                                    timeout={300000}
                                />
                            </div>
                        </div>
                    )}
                </div>
                <ScreenShareSelectionTabs onClose={onClose} />
            </div>
        </div>
    )
}

function ScreenList({ type, onClose }: { type: 'screens' | 'applications'; onClose: () => void }) {
    const { state } = useContext(UserContext) as UserContextType

    const [selectedDesktopId, set_selectedDesktopId] = useState(state.mediaSelection.desktop)

    const { logUserActivityWithContext } = useAnalytics()

    const onSelectHandler = (id: string) => {
        set_selectedDesktopId(id)
    }

    const list = state.mediaSources.desktop.filter((source) => {
        const isScreen = !!source.display_id
        if (type === 'screens') {
            return isScreen
        } else {
            return !isScreen
        }
    })

    if (!state.mediaPermissions.desktop) {
        return <PermissionRequiredFragment />
    }

    return (
        <div className="flex flex-col w-full h-full">
            <div className="flex flex-wrap flex-grow items-start content-start w-full px-3 py-3 overflow-y-auto">
                {/* the "None" item */}
                <DesktopSourcesListItem
                    source={{ name: 'None' }}
                    isSelected={isNullMediaDeviceId(selectedDesktopId)}
                    onSelect={() => onSelectHandler('_none')}
                />
                {/* the rest of the list */}
                {list.map((source) => {
                    const isSelected = selectedDesktopId === source.id
                    return (
                        <DesktopSourcesListItem
                            key={source.id}
                            source={source}
                            isSelected={isSelected}
                            onSelect={() => {
                                onSelectHandler(source.id)
                                logUserActivityWithContext(AnalyticsEvents.CLICKED_SCREEN_SHARE_SELECTION_BUTTON, {
                                    componentSource: 'ScreenShareSelectionPage',
                                    source,
                                })
                            }}
                        />
                    )
                })}
            </div>
            <BottomTray selectedDesktopId={selectedDesktopId} onClose={onClose} />
        </div>
    )
}

function ScreenShareSelectionTabs({ onClose }: { onClose: () => void }) {
    const { width: _width, height: _height, nodeRef } = useDimensionObserver()

    const [fallbackWidth, setFallbackWidth] = useState(0)
    const [fallbackHeight, setFallbackHeight] = useState(0)

    // set fallback width and height for the case useDimensionObserver does not
    // work in a modal in fullscreen mode.
    useNonIdempotentEffect(() => {
        const handler = async (data: { windowName: string }) => {
            if (data.windowName === 'ScreenShareSelectionModal') {
                await sleep(100)
                if (nodeRef.current) {
                    const width = nodeRef.current.clientWidth
                    const height = nodeRef.current.clientHeight
                    setFallbackWidth(width)
                    setFallbackHeight(height)
                }
            }
        }

        ElectronIPC.registerWindowChangedHandler(handler)

        if (nodeRef.current) {
            handler({ windowName: 'ScreenShareSelectionModal' })
        }

        return () => ElectronIPC.removeWindowChangedHandler(handler)
    }, [nodeRef.current])

    const width = _width || fallbackWidth
    const height = _height || fallbackHeight

    const tabs_offset = 50

    return (
        <div className="h-full w-full overflow-hidden" ref={nodeRef}>
            <DiagonalTabs
                initialIndex={0}
                onTabChange={() => {}}
                containerWidth={width}
                containerHeight={height - tabs_offset - 10 /*margin*/}
                childrenByTabIndex={[
                    <ScreenList type="screens" onClose={onClose} />,
                    <ScreenList type="applications" onClose={onClose} />,
                ]}
                tabs={[
                    { id: 'screens', label: 'Screens' },
                    { id: 'applications', label: 'Applications' },
                ]}
            />
        </div>
    )
}
